// /src/components/calendar/EventModal.js
import { useState } from "react";
import { X, Loader } from "lucide-react";

export default function EventModal({ token, defaultDate, event, onClose, onSaved }) {
  const isEdit = !!event;
  const pad = n => String(n).padStart(2, "0");
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

  const defaultStart = event?.start?.dateTime ? fmt(new Date(event.start.dateTime)) : `${fmt(defaultDate).slice(0, 11)}09:00`;
  const defaultEnd = event?.end?.dateTime ? fmt(new Date(event.end.dateTime)) : `${fmt(defaultDate).slice(0, 11)}10:00`;

  const [summary, setSummary] = useState(event?.summary || "");
  const [description, setDescription] = useState(event?.description || "");
  const [location, setLocation] = useState(event?.location || "");
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(defaultEnd);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const body = {
      summary,
      description,
      location,
      start: { dateTime: new Date(start).toISOString(), timeZone: tz },
      end: { dateTime: new Date(end).toISOString(), timeZone: tz },
    };

    try {
      const url = isEdit ? `/api/gcalendarevents?eventId=${event.id}` : "/api/gcalendarevents";
      const res = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.error) { setError(data.error || "Failed to save event"); setSaving(false); return; }
      onSaved(data, isEdit);
    } catch {
      setError("Network error. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div className="card" style={{ width: "100%", maxWidth: 480, padding: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>{isEdit ? "Edit Event" : "New Event"}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Title *</label>
            <input className="form-input" value={summary} onChange={e => setSummary(e.target.value)} placeholder="Meeting with client" required />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Start</label>
              <input className="form-input" type="datetime-local" value={start} onChange={e => setStart(e.target.value)} required />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">End</label>
              <input className="form-input" type="datetime-local" value={end} onChange={e => setEnd(e.target.value)} required />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Location</label>
            <input className="form-input" value={location} onChange={e => setLocation(e.target.value)} placeholder="Office, Zoom link, etc." />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Description</label>
            <textarea className="form-input" value={description} onChange={e => setDescription(e.target.value)} placeholder="Notes about this event..." rows={3} style={{ resize: "vertical" }} />
          </div>
          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
              {error}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ gap: 6 }}>
              {saving ? <><Loader size={14} style={{ animation: "spin 0.8s linear infinite" }} /> Saving...</> : isEdit ? "Save Changes" : "Create Event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}